import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mergeSafetyValveData } from './safety-valve-calibration-profile';
import { certificateImageAsDataUrl, drawCertificateFooter, drawCertificateHeader, drawReferenceStandardsTable } from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

function num(value) { const number = Number(value); return Number.isFinite(number) ? number : null; }
function fmt(value, decimals = 3) { const number = num(value); return number === null ? '-' : number.toFixed(decimals).replace('.', ','); }
function safe(value) {
  return String(value ?? '-')
    .replace(/²/g, '2').replace(/₂/g, '2').replace(/³/g, '3')
    .replace(/≤/g, '<=').replace(/≥/g, '>=').replace(/±/g, '+/-');
}
function dmy(value) {
  if (!value) return '-';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  return Number.isNaN(date.getTime()) ? '-' : `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}
function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '-';
}
function status(value) {
  const key = String(value || '').toLowerCase();
  if (['satisfactory', 'pass', 'passed', 'ok'].includes(key)) return 'SATISFACTORIO';
  if (['fail', 'failed', 'unsatisfactory'].includes(key)) return 'NO SATISFACTORIO';
  if (['not_applicable', 'na'].includes(key)) return 'NO APLICA';
  if (['observation'].includes(key)) return 'CON OBSERVACION';
  if (['not_checked', 'not_performed', ''].includes(key)) return 'NO VERIFICADO';
  return safe(value).toUpperCase();
}
async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}
function header(doc, { logo, number, page, pages, first }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first });
}
function footer(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}
function sectionTitle(doc, title, y) {
  doc.setFillColor(180, 215, 225); doc.rect(14, y, 182, 7, 'F');
  doc.setTextColor(0); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(safe(title), 16, y + 4.8); doc.setTextColor(0);
  return y + 9;
}
function linePair(doc, label, value, x, y, width = 88) {
  doc.setDrawColor(190); doc.rect(x, y, width, 8);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.text(safe(label), x + 2, y + 3.2);
  doc.setFont('helvetica', 'normal'); doc.text(safe(value), x + 2, y + 6.4, { maxWidth: width - 4 });
}
function table(doc, options) {
  autoTable(doc, {
    theme: 'grid', margin: { left: 14, right: 14 },
    styles: { font: 'helvetica', fontSize: 7.4, cellPadding: 1.5, textColor: 20, lineColor: [175, 180, 185], lineWidth: 0.15 },
    headStyles: { fillColor: [180, 215, 225], textColor: 0, fontStyle: 'bold', halign: 'center' },
    ...options,
  });
  return doc.lastAutoTable.finalY;
}

function pageOne(doc, { session, instrument, familyData, metrics }) {
  let y = sectionTitle(doc, '1. DATOS DEL CLIENTE Y DEL INSTRUMENTO', 63);
  linePair(doc, 'Cliente', session.client_name, 14, y, 88); linePair(doc, 'N° cliente', session.client_number, 108, y, 88); y += 9;
  linePair(doc, 'Domicilio', [session.client_address, session.client_city, session.client_province].filter(Boolean).join(' · '), 14, y, 182); y += 9;
  linePair(doc, 'Objeto', session.instrument_object || instrument?.type, 14, y, 88); linePair(doc, 'TAG', session.instrument_tag || instrument?.tag, 108, y, 88); y += 9;
  linePair(doc, 'Marca / modelo', [session.instrument_brand, session.instrument_model].filter(Boolean).join(' · '), 14, y, 88); linePair(doc, 'Serie', session.instrument_serial || instrument?.serial, 108, y, 88); y += 11;

  y = sectionTitle(doc, '2. DATOS DE CALIBRACION', y);
  linePair(doc, 'Fecha de calibracion', dmy(session.calibration_date), 14, y, 56); linePair(doc, 'Fecha de emision', dmy(session.report_issue_date), 77, y, 56); linePair(doc, 'Valido hasta', dmy(session.valid_until), 140, y, 56); y += 9;
  linePair(doc, 'Lugar', session.instrument_location, 14, y, 56); linePair(doc, 'Perfil', familyData.profile_mode === 'vacuum' ? 'Seguridad a vacio' : 'Seguridad a presion', 77, y, 56); linePair(doc, 'Unidad', familyData.pressure_unit, 140, y, 56); y += 9;
  linePair(doc, 'Presion de timbre', `${fmt(familyData.set_pressure)} ${familyData.pressure_unit}`, 14, y, 56); linePair(doc, 'Presion de operacion', `${fmt(familyData.operating_pressure)} ${familyData.pressure_unit}`, 77, y, 56); linePair(doc, 'Temp. de trabajo', num(familyData.working_temperature_c) === null ? '-' : `${fmt(familyData.working_temperature_c, 1)} °C`, 140, y, 56); y += 9;
  linePair(doc, 'Fluido de servicio', familyData.service_fluid, 14, y, 56); linePair(doc, 'Entrada / salida', `${familyData.inlet_size || '-'} / ${familyData.outlet_size || '-'}`, 77, y, 56); linePair(doc, 'Conexion', familyData.connection_type, 140, y, 56); y += 11;

  y = sectionTitle(doc, '3. CONDICIONES AMBIENTALES', y);
  y = table(doc, {
    startY: y,
    head: [['Momento', 'Temperatura [°C]', 'Presion [hPa]', 'Humedad [%HR]']],
    body: familyData.environment_readings.map((row) => [safe(row.label), fmt(row.temp_c, 1), fmt(row.pressure_hpa, 1), fmt(row.humidity_rh, 1)]),
  }) + 3;

  y = sectionTitle(doc, '4. RESULTADOS', y);
  y = table(doc, {
    startY: y,
    head: [['Magnitud', 'Resultado', 'Error / diferencia', 'U (k=2)', 'Evaluacion']],
    body: [
      ['Presion de apertura', `${fmt(metrics.opening.mean)} ${metrics.unit}`, `${fmt(metrics.error_abs)} ${metrics.unit} (${fmt(metrics.error_pct, 2)} %)`, `${fmt(metrics.opening.u_expanded)} ${metrics.unit}`, metrics.opening_pass == null ? 'INFORMATIVO' : metrics.opening_pass ? 'SATISFACTORIO' : 'NO SATISFACTORIO'],
      // La evaluacion es POR ENSAYO, asi que el certificado debe mostrar el peor
      // intento: si no, declara NO SATISFACTORIO sin exhibir el valor que lo causa.
      ...(Array.isArray(metrics.opening_trials) && metrics.opening_trials.length > 1
        ? [[
            'Apertura - peor ensayo',
            (() => {
              const worst = metrics.opening_trials.reduce((acc, trial) => (
                trial.error_pct === null ? acc
                  : (!acc || Math.abs(trial.error_pct) > Math.abs(acc.error_pct) ? trial : acc)
              ), null);
              return worst ? `${fmt(worst.opening)} ${metrics.unit} (ensayo ${worst.trial_no})` : '-';
            })(),
            metrics.worst_opening_error_pct == null ? '-' : `${fmt(metrics.worst_opening_error_pct, 2)} %`,
            '-',
            metrics.opening_pass == null ? 'INFORMATIVO' : metrics.opening_pass ? 'SATISFACTORIO' : 'NO SATISFACTORIO',
          ]]
        : []),
      ['Presion de cierre', metrics.closing ? `${fmt(metrics.closing.mean)} ${metrics.unit}` : 'NO REQUERIDA', metrics.blowdown_percent == null ? '-' : `Blowdown ${fmt(metrics.blowdown_percent, 2)} %`, metrics.closing ? `${fmt(metrics.closing.u_expanded)} ${metrics.unit}` : '-', metrics.blowdown_pass == null ? 'INFORMATIVO' : metrics.blowdown_pass ? 'SATISFACTORIO' : 'NO SATISFACTORIO'],
      ['Hermeticidad', status(familyData.leak_test_result), num(familyData.leak_test_pressure) === null ? '-' : `Ensayo a ${fmt(familyData.leak_test_pressure)} ${metrics.unit}`, '-', metrics.leak_pass == null ? 'INFORMATIVO' : metrics.leak_pass ? 'SATISFACTORIO' : 'NO SATISFACTORIO'],
    ],
    columnStyles: { 0: { cellWidth: 35 }, 4: { cellWidth: 33, halign: 'center' } },
  }) + 4;
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  const conclusion = metrics.evaluation_mode === 'report_only'
    ? 'Los resultados se informan sin declaracion de conformidad, dado que no se especifico una tolerancia contractual o reglamentaria.'
    // ISO/IEC 17025 7.8.6 exige declarar la regla de decision aplicada, incluido
    // el criterio de tolerancia; con la regla dual de ASME conviene explicitar
    // si se aplico el tramo absoluto o el porcentual.
    : `Criterio de tolerancia: ${
        metrics.opening_tolerance_rule === 'asme_absolute'
          ? `ASME UG-134(d)(1), tramo absoluto (+/-${fmt(metrics.opening_tolerance_abs)} ${metrics.unit}, equivalente a ${fmt(metrics.opening_tolerance_percent, 2)} % del timbre)`
          : metrics.opening_tolerance_rule === 'asme_percent'
            ? `ASME UG-134(d)(1), tramo porcentual (+/-${fmt(metrics.opening_tolerance_percent, 2)} % del timbre)`
            : `tolerancia declarada (+/-${fmt(metrics.opening_tolerance_percent, 2)} % del timbre)`
      }. Evaluacion por ensayo individual. Regla de decision: ${metrics.decision_rule === 'guard_band' ? 'banda de guarda incluyendo incertidumbre' : 'comparacion directa contra tolerancia'}. Resultado general: ${metrics.overall_pass ? 'SATISFACTORIO' : 'NO SATISFACTORIO'}.`;
  doc.text(safe(conclusion), 14, y, { maxWidth: 182 });
}

function pageTwo(doc, { session, familyData, metrics, signature }) {
  const snapshot = familyData.standard_configuration?.reference || {};
  let y = sectionTitle(doc, '5. LECTURAS Y CORRECCIONES', 33);
  const openings = metrics.opening_readings || [];
  const closings = metrics.closing_readings || [];
  y = table(doc, {
    startY: y,
    head: [['Ensayo', 'Apertura leida', 'Correccion', 'Apertura corregida', 'Cierre leido', 'Correccion', 'Cierre corregido']],
    body: Array.from({ length: familyData.repeat_count }, (_, index) => {
      const opening = openings[index]; const closing = closings[index];
      return [index + 1, fmt(opening?.raw), fmt(opening?.correction), fmt(opening?.corrected), fmt(closing?.raw), fmt(closing?.correction), fmt(closing?.corrected)];
    }),
  }) + 3;

  y = sectionTitle(doc, '6. METODO Y TRAZABILIDAD', y);
  linePair(doc, 'Metodo', familyData.method, 14, y, 182); y += 9;
  linePair(doc, 'Procedimiento', familyData.procedure, 14, y, 182); y += 9;
  y = drawReferenceStandardsTable(doc, { startY: y, references: [{ ...snapshot, usage: 'Referencia' }] }) + 8;

  y = sectionTitle(doc, '7. PRESUPUESTO DE INCERTIDUMBRE', y);
  y = table(doc, {
    startY: y,
    head: [['Resultado', 'u patron', 'u resolucion', 'u repetibilidad', 'u histeresis / otras', 'uc', 'U (k=2)']],
    body: [
      ['Apertura', fmt(metrics.opening.u_standard), fmt(metrics.opening.u_resolution), fmt(metrics.opening.u_repeatability), fmt(Math.hypot(metrics.opening.u_hysteresis, metrics.opening.u_other)), fmt(metrics.opening.uc), fmt(metrics.opening.u_expanded)],
      ...(metrics.closing ? [['Cierre', fmt(metrics.closing.u_standard), fmt(metrics.closing.u_resolution), fmt(metrics.closing.u_repeatability), fmt(Math.hypot(metrics.closing.u_hysteresis, metrics.closing.u_other)), fmt(metrics.closing.uc), fmt(metrics.closing.u_expanded)]] : []),
    ],
  }) + 3;
  doc.setFontSize(7.5); doc.text(safe(`La incertidumbre expandida se obtiene como U = 2 · uc, con cobertura aproximada del 95 %. Para el blowdown se obtuvo U = ${fmt(metrics.blowdown_u_expanded_percent, 2)} %.`), 14, y, { maxWidth: 182 }); y += 9;

  y = sectionTitle(doc, '8. INSPECCION Y TAREAS REALIZADAS', y);
  const inspectionRows = [
    ['Identificacion', status(familyData.inspection.label_status), 'Precinto', status(familyData.inspection.seal_status)],
    ['Estado externo', status(familyData.inspection.external_status), 'Estado interno', status(familyData.inspection.internal_status)],
  ];
  y = table(doc, { startY: y, head: [['Control', 'Resultado', 'Control', 'Resultado']], body: inspectionRows }) + 2;
  const tasks = Object.entries(familyData.procedures).filter(([, done]) => done).map(([key]) => ({ cleaning: 'Limpieza', seating: 'Ajuste de asiento', painting: 'Pintura', tests: 'Pruebas', recalibration: 'Recalibracion', sealing: 'Precintado', photographs: 'Fotografias' }[key] || key));
  doc.setFontSize(8); doc.text(`Tareas realizadas: ${safe(tasks.join(', ') || 'Ninguna registrada')}.`, 14, y, { maxWidth: 182 }); y += 8;
  doc.text(`Observaciones: ${safe(session.notes || 'Sin observaciones.')}`, 14, y, { maxWidth: 182 });

  const height = doc.internal.pageSize.getHeight();
  if (signature) doc.addImage(signature, 'PNG', 129, height - 46, 48, 22, undefined, 'FAST');
  doc.setDrawColor(80); doc.line(118, height - 23, 188, height - 23);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.text('Responsable del Laboratorio', 153, height - 19, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.text('Daniel Kemeny', 153, height - 15, { align: 'center' });
}

export async function generateSafetyValveNoSacCertificatePdf({ session, instrument, results = [] }, options = {}) {
  if (!session) throw new Error('La sesion es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');
  const familyData = mergeSafetyValveData(session.family_data);
  const metrics = familyData.result_summary;
  if (!metrics?.opening) throw new Error('La sesion no conserva el resumen metrologico de la valvula.');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([imageAsDataUrl(options.logoUrl || LOGO_URL), imageAsDataUrl(options.signatureUrl || SIGNATURE_URL)]);
  const number = certificateNumber(options.serviceOrderCode, session.service_order_code, session.certificate_number, instrument?.lab_id);
  pageOne(doc, { session, instrument, familyData, metrics }); doc.addPage(); pageTwo(doc, { session, familyData, metrics, signature });
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) { doc.setPage(page); header(doc, { logo, number, page, pages, first: page === 1 }); footer(doc, page, pages); }
  if (options.returnBlob) return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  doc.save(options.filename || `${number !== '-' ? number : 'R-DT-PC-VALVE'}.pdf`);
  return null;
}

